/**
 * @file messages.ts
 *
 * react-intl message descriptors for the Online Shop Admin UI. Explicit ids
 * (prefixed `admin.`) keep extraction working under CRA without ejecting;
 * `pnpm --filter client i18n:extract` pulls these into the source catalog.
 */

import { defineMessages } from 'react-intl';

export const adminMessages = defineMessages({
  shopTitle: {
    id: 'admin.shop.title',
    defaultMessage: 'Online Shop',
  },
  navMenuLabel: {
    id: 'admin.nav.menuLabel',
    defaultMessage: 'Shop section',
  },
  navProducts: {
    id: 'admin.nav.products',
    defaultMessage: 'Products',
  },
  navCategories: {
    id: 'admin.nav.categories',
    defaultMessage: 'Categories',
  },
  navOrders: {
    id: 'admin.nav.orders',
    defaultMessage: 'Orders',
  },
  navCoupons: {
    id: 'admin.nav.coupons',
    defaultMessage: 'Coupons',
  },
  navShipping: {
    id: 'admin.nav.shipping',
    defaultMessage: 'Shipping',
  },
  navPayments: {
    id: 'admin.nav.payments',
    defaultMessage: 'Payments & VAT',
  },
  navSettings: {
    id: 'admin.nav.settings',
    defaultMessage: 'Settings',
  },
  accountMenuLabel: {
    id: 'admin.account.menuLabel',
    defaultMessage: 'Account',
  },
  accountClose: {
    id: 'admin.account.close',
    defaultMessage: 'Close',
  },
  accountPreferences: {
    id: 'admin.account.preferences',
    defaultMessage: 'Preferences',
  },
  accountLogout: {
    id: 'admin.account.logout',
    defaultMessage: 'Log out',
  },
  settingsTitle: {
    id: 'admin.settings.title',
    defaultMessage: 'Settings',
  },
  settingsShopfrontTitle: {
    id: 'admin.settings.shopfront.title',
    defaultMessage: 'Shopfront language',
  },
  settingsShopfrontDescription: {
    id: 'admin.settings.shopfront.description',
    defaultMessage:
      'The language shown to customers on your storefront and in transactional emails.',
  },
  settingsShopInfoTitle: {
    id: 'admin.settings.shopInfo.title',
    defaultMessage: 'Shop information',
  },
  settingsShopName: {
    id: 'admin.settings.shopInfo.shopName',
    defaultMessage: 'Shop name',
  },
  settingsStreetAddress: {
    id: 'admin.settings.shopInfo.streetAddress',
    defaultMessage: 'Street address',
  },
  settingsZipCode: {
    id: 'admin.settings.shopInfo.zipCode',
    defaultMessage: 'Zip code',
  },
  settingsCity: {
    id: 'admin.settings.shopInfo.city',
    defaultMessage: 'City',
  },
  settingsCountry: {
    id: 'admin.settings.shopInfo.country',
    defaultMessage: 'Country',
  },
  settingsCountryPlaceholder: {
    id: 'admin.settings.shopInfo.countryPlaceholder',
    defaultMessage: 'Select country',
  },
  settingsEmailPrefsTitle: {
    id: 'admin.settings.emailPreferences.title',
    defaultMessage: 'Email preferences',
  },
  settingsEmailPrefsDescription: {
    id: 'admin.settings.emailPreferences.description',
    defaultMessage: 'Customise your shop emails - like order confirmations.',
  },
  settingsEmail: {
    id: 'admin.settings.emailPreferences.email',
    defaultMessage: 'Email',
  },
  settingsEmailLanguage: {
    id: 'admin.settings.emailPreferences.emailLanguage',
    defaultMessage: 'Email language',
  },
  settingsEmailLanguageDescription: {
    id: 'admin.settings.emailPreferences.emailLanguageDescription',
    defaultMessage: 'Language for emails sent to your customers from your shop.',
  },
  settingsEmailLanguagePlaceholder: {
    id: 'admin.settings.emailPreferences.emailLanguagePlaceholder',
    defaultMessage: 'Select a language',
  },
  settingsCurrencyPosition: {
    id: 'admin.settings.emailPreferences.currencyPosition',
    defaultMessage: 'Currency position',
  },
  settingsDateFormat: {
    id: 'admin.settings.emailPreferences.dateFormat',
    defaultMessage: 'Date format',
  },
  settingsNumberFormat: {
    id: 'admin.settings.emailPreferences.numberFormat',
    defaultMessage: 'Number format',
  },
  settingsRegionCurrencyTitle: {
    id: 'admin.settings.regionAndCurrency.title',
    defaultMessage: 'Region & currency',
  },
  settingsCurrency: {
    id: 'admin.settings.regionAndCurrency.currency',
    defaultMessage: 'Currency',
  },
  settingsWeightUnit: {
    id: 'admin.settings.regionAndCurrency.weightUnit',
    defaultMessage: 'Weight unit',
  },
  settingsTimezone: {
    id: 'admin.settings.regionAndCurrency.timezone',
    defaultMessage: 'Time zone',
  },
  settingsTimezoneDescription: {
    id: 'admin.settings.regionAndCurrency.timezoneDescription',
    defaultMessage: 'Used when showing order dates and sending order emails.',
  },
  settingsSave: {
    id: 'admin.settings.save',
    defaultMessage: 'Save',
  },
  settingsSaving: {
    id: 'admin.settings.saving',
    defaultMessage: 'Saving…',
  },
  settingsLoadErrorTitle: {
    id: 'admin.settings.loadErrorTitle',
    defaultMessage: 'Couldn’t load settings',
  },
  settingsLoadErrorDescription: {
    id: 'admin.settings.loadErrorDescription',
    defaultMessage:
      'We couldn’t load your shop settings. Please try again — saving now could overwrite your saved settings.',
  },
  settingsLoadRetry: {
    id: 'admin.settings.loadRetry',
    defaultMessage: 'Try again',
  },
  settingsRequired: {
    id: 'admin.settings.validation.required',
    defaultMessage: 'This field is required. Please fill it in to continue.',
  },
  settingsInvalidEmail: {
    id: 'admin.settings.validation.email',
    defaultMessage: 'Invalid format. Please check and try again.',
  },
  comingSoonTitle: {
    id: 'admin.comingSoon.title',
    defaultMessage: 'Coming soon',
  },
  comingSoonBody: {
    id: 'admin.comingSoon.body',
    defaultMessage: 'The {section} section is under construction.',
  },
  loginTitle: {
    id: 'admin.login.title',
    defaultMessage: 'Admin sign in',
  },
  loginSubtitle: {
    id: 'admin.login.subtitle',
    defaultMessage: 'WSB Commerce Platform',
  },
  loginUsername: {
    id: 'admin.login.username',
    defaultMessage: 'Username',
  },
  loginPassword: {
    id: 'admin.login.password',
    defaultMessage: 'Password',
  },
  loginProjectUuid: {
    id: 'admin.login.projectUuid',
    defaultMessage: 'Project UUID',
  },
  loginSubmit: {
    id: 'admin.login.submit',
    defaultMessage: 'Sign in',
  },
  loginError: {
    id: 'admin.login.error',
    defaultMessage: 'Invalid username or password.',
  },

  // Products
  productsTitle: {
    id: 'admin.products.title',
    defaultMessage: 'Products',
  },
  productsAdd: {
    id: 'admin.products.add',
    defaultMessage: 'Add product',
  },
  productsSearchPlaceholder: {
    id: 'admin.products.searchPlaceholder',
    defaultMessage: 'Search products',
  },
  productsSearchLabel: {
    id: 'admin.products.searchLabel',
    defaultMessage: 'Search products',
  },
  productsLoading: {
    id: 'admin.products.loading',
    defaultMessage: 'Loading products…',
  },
  productsEmptyTitle: {
    id: 'admin.products.emptyTitle',
    defaultMessage: 'No products yet',
  },
  productsEmptyDescription: {
    id: 'admin.products.emptyDescription',
    defaultMessage: 'Start building your store by adding your first product.',
  },
  productsNoResultsTitle: {
    id: 'admin.products.noResultsTitle',
    defaultMessage: 'No results',
  },
  productsNoResultsDescription: {
    id: 'admin.products.noResultsDescription',
    defaultMessage: 'Try another search term or filter.',
  },
  productsViewGrid: {
    id: 'admin.products.viewGrid',
    defaultMessage: 'Grid',
  },
  productsViewList: {
    id: 'admin.products.viewList',
    defaultMessage: 'List',
  },
  productsFilterStock: {
    id: 'admin.products.filterStock',
    defaultMessage: 'Stock',
  },
  productsFilterCategory: {
    id: 'admin.products.filterCategory',
    defaultMessage: 'Category',
  },
  productsFilterApply: {
    id: 'admin.products.filterApply',
    defaultMessage: 'Apply',
  },
  productsFilterClear: {
    id: 'admin.products.filterClear',
    defaultMessage: 'Clear',
  },
  productsFilterCancel: {
    id: 'admin.products.filterCancel',
    defaultMessage: 'Cancel',
  },
  productsFilterResetStock: {
    id: 'admin.products.filterResetStock',
    defaultMessage: 'Reset stock filter',
  },
  productsFilterResetCategory: {
    id: 'admin.products.filterResetCategory',
    defaultMessage: 'Reset category filter',
  },
  productsFilterResetStatus: {
    id: 'admin.products.filterResetStatus',
    defaultMessage: 'Reset status filter',
  },
  productsFieldName: {
    id: 'admin.products.fieldName',
    defaultMessage: 'Name',
  },
  productsFieldStatus: {
    id: 'admin.products.fieldStatus',
    defaultMessage: 'Status',
  },
  productsFieldType: {
    id: 'admin.products.fieldType',
    defaultMessage: 'Type',
  },
  productsFieldCategory: {
    id: 'admin.products.fieldCategory',
    defaultMessage: 'Category',
  },
  productsFieldStock: {
    id: 'admin.products.fieldStock',
    defaultMessage: 'Stock',
  },
  productsFieldPrice: {
    id: 'admin.products.fieldPrice',
    defaultMessage: 'Price',
  },
  productsFieldSku: {
    id: 'admin.products.fieldSku',
    defaultMessage: 'SKU',
  },
  productsFieldCreated: {
    id: 'admin.products.fieldCreated',
    defaultMessage: 'Created',
  },
  productsMoreCategories: {
    id: 'admin.products.moreCategories',
    defaultMessage: '+{count} more',
  },
  productsStockInstock: {
    id: 'admin.products.stockInstock',
    defaultMessage: 'In stock',
  },
  productsStockOutofstock: {
    id: 'admin.products.stockOutofstock',
    defaultMessage: 'Out of stock',
  },
  productsStockOnbackorder: {
    id: 'admin.products.stockOnbackorder',
    defaultMessage: 'On backorder',
  },
  productsStockPartial: {
    id: 'admin.products.stockPartial',
    defaultMessage: 'Some out of stock',
  },
  productsTypeSimple: {
    id: 'admin.products.typeSimple',
    defaultMessage: 'Simple',
  },
  productsTypeDigital: {
    id: 'admin.products.typeDigital',
    defaultMessage: 'Digital',
  },
  productsTypeVariants: {
    id: 'admin.products.typeVariants',
    defaultMessage: 'With {count} variants',
  },
  productsCardVariants: {
    id: 'admin.products.cardVariants',
    defaultMessage: '{count} variants',
  },
  productsVariantsOutOfStock: {
    id: 'admin.products.variantsOutOfStock',
    defaultMessage: '{count} of {total} variants out of stock',
  },
  productsFilterStatus: {
    id: 'admin.products.filterStatus',
    defaultMessage: 'Status',
  },
  productsStatusPublished: {
    id: 'admin.products.statusPublished',
    // "Visible", not "Published": the engine's `active` is published-but-listable, and its
    // real counterpart is `hidden` (published, reachable by link, unlisted) — the same
    // Visible/Draft/Hidden vocabulary WSB uses for commerce-engine shops.
    defaultMessage: 'Visible',
  },
  productsStatusDraft: {
    id: 'admin.products.statusDraft',
    defaultMessage: 'Draft',
  },
  productsStatusHidden: {
    id: 'admin.products.statusHidden',
    defaultMessage: 'Hidden',
  },
  productsActions: {
    id: 'admin.products.actions',
    defaultMessage: 'Product actions',
  },
  productsMenuView: {
    id: 'admin.products.menuView',
    defaultMessage: 'View',
  },
  productsMenuEdit: {
    id: 'admin.products.menuEdit',
    defaultMessage: 'Edit',
  },
  productsMenuDuplicate: {
    id: 'admin.products.menuDuplicate',
    defaultMessage: 'Duplicate',
  },
  productsMenuDelete: {
    id: 'admin.products.menuDelete',
    defaultMessage: 'Delete',
  },
  productsClose: {
    id: 'admin.products.close',
    defaultMessage: 'Close',
  },
  paginationPrevPage: {
    id: 'admin.pagination.prevPage',
    defaultMessage: 'Previous page',
  },
  paginationNextPage: {
    id: 'admin.pagination.nextPage',
    defaultMessage: 'Next page',
  },
  paginationPageLabel: {
    id: 'admin.pagination.pageLabel',
    defaultMessage: 'Page {page}',
  },
  toastSaved: {
    id: 'admin.toast.saved',
    defaultMessage: 'Saved successfully',
  },
  toastDeleted: {
    id: 'admin.toast.deleted',
    defaultMessage: 'Deleted successfully',
  },
  toastError: {
    id: 'admin.toast.error',
    defaultMessage: 'Something went wrong. Please try again.',
  },
  productsDrawerShortDescription: {
    id: 'admin.products.drawerShortDescription',
    defaultMessage: 'Short description',
  },
  productsDrawerLongDescription: {
    id: 'admin.products.drawerLongDescription',
    defaultMessage: 'Long description',
  },
  productsEdit: {
    id: 'admin.products.edit',
    defaultMessage: 'Edit',
  },
  productsDelete: {
    id: 'admin.products.delete',
    defaultMessage: 'Delete',
  },
  productsDeleteTitle: {
    id: 'admin.products.deleteTitle',
    defaultMessage: 'Delete product',
  },
  productsDeleteMessage: {
    id: 'admin.products.deleteMessage',
    defaultMessage:
      'Are you sure you want to delete “{name}”? This action cannot be undone.',
  },
  productsDeleteLoading: {
    id: 'admin.products.deleteLoading',
    defaultMessage: 'Deleting…',
  },
  productsDeleteError: {
    id: 'admin.products.deleteError',
    defaultMessage: 'Couldn’t delete the product. Please try again.',
  },
  productFormTitle: {
    id: 'admin.products.formTitle',
    defaultMessage: 'Product form',
  },

  // Product form — titles & actions
  formBack: { id: 'admin.products.form.back', defaultMessage: 'Back' },
  formAddTitle: { id: 'admin.products.form.addTitle', defaultMessage: 'Add new product' },
  formEditTitle: { id: 'admin.products.form.editTitle', defaultMessage: 'Edit product' },
  formDuplicateTitle: {
    id: 'admin.products.form.duplicateTitle',
    defaultMessage: 'Duplicate product',
  },
  formSave: { id: 'admin.products.form.save', defaultMessage: 'Save' },
  formCreate: { id: 'admin.products.form.create', defaultMessage: 'Create' },
  formSaving: { id: 'admin.products.form.saving', defaultMessage: 'Saving…' },
  formCancel: { id: 'admin.products.form.cancel', defaultMessage: 'Cancel' },
  formLoading: { id: 'admin.products.form.loading', defaultMessage: 'Loading product…' },
  formUnsavedTitle: {
    id: 'admin.products.form.unsavedTitle',
    defaultMessage: 'Unsaved changes',
  },
  formUnsavedMessage: {
    id: 'admin.products.form.unsavedMessage',
    defaultMessage: 'You have unsaved changes. Are you sure you want to leave?',
  },
  formUnsavedConfirm: { id: 'admin.products.form.unsavedConfirm', defaultMessage: 'Leave' },
  formUnsavedCancel: { id: 'admin.products.form.unsavedCancel', defaultMessage: 'Stay' },
  formLoadErrorTitle: {
    id: 'admin.products.form.loadErrorTitle',
    defaultMessage: 'Couldn’t load product',
  },
  formLoadErrorDescription: {
    id: 'admin.products.form.loadErrorDescription',
    defaultMessage: 'We couldn’t load this product. Please go back and try again.',
  },
  formSaveError: {
    id: 'admin.products.form.saveError',
    defaultMessage: 'Couldn’t save the product. Please try again.',
  },
  formSaveErrorStale: {
    id: 'admin.products.form.saveErrorStale',
    defaultMessage: 'Someone else changed this product. Reload and retry.',
  },
  formSaveErrorSkuConflict: {
    id: 'admin.products.form.saveErrorSkuConflict',
    defaultMessage: 'That SKU is already used by another product. Choose a unique SKU.',
  },
  formSaveErrorSlugConflict: {
    id: 'admin.products.form.saveErrorSlugConflict',
    defaultMessage: 'A product with this name already exists. Choose a different name.',
  },
  formSaveErrorUniformPriceRefused: {
    id: 'admin.products.form.saveErrorUniformPriceRefused',
    defaultMessage:
      'This change would overwrite the individual variant prices. Edit the variants instead.',
  },
  formSaveErrorNoEnabledVariant: {
    id: 'admin.products.form.saveErrorNoEnabledVariant',
    defaultMessage: 'Keep at least one variant active before saving.',
  },
  formSaveErrorVariantImageInvalid: {
    id: 'admin.products.form.saveErrorVariantImageInvalid',
    defaultMessage: "A variant image must be one of the product's own images. Re-pick it and save again.",
  },
  formSaveErrorPriceInvalid: {
    id: 'admin.products.form.saveErrorPriceInvalid',
    defaultMessage: 'A sale price must be lower than the regular price, with a valid sale window.',
  },
  formSaveErrorVariantOverrideInvalid: {
    id: 'admin.products.form.saveErrorVariantOverrideInvalid',
    defaultMessage: 'A variant edit no longer matches the product’s options. Reload the product and try again.',
  },
  formSaveErrorTooManyVariants: {
    id: 'admin.products.form.saveErrorTooManyVariants',
    defaultMessage: 'This product has too many variants. Remove some option values.',
  },
  formSaveErrorOptionDuplicate: {
    id: 'admin.products.form.saveErrorOptionDuplicate',
    defaultMessage: 'Two options have the same name. Give each option a unique name.',
  },

  // Product form — product type
  formProductType: { id: 'admin.products.form.productType', defaultMessage: 'Product type' },
  formPhysical: { id: 'admin.products.form.physical', defaultMessage: 'Physical' },
  formPhysicalDescription: {
    id: 'admin.products.form.physicalDescription',
    defaultMessage: 'e.g. t-shirt, shoes, skateboard',
  },
  formDigital: { id: 'admin.products.form.digital', defaultMessage: 'Digital' },
  formDigitalDescription: {
    id: 'admin.products.form.digitalDescription',
    defaultMessage: 'e.g. eBook, printable or digital album',
  },

  // Product form — media
  formMedia: { id: 'admin.products.form.media', defaultMessage: 'Media' },
  formDownloadableFiles: {
    id: 'admin.products.form.downloadableFiles',
    defaultMessage: 'Downloadable files',
  },
  formMediaDropTitle: {
    id: 'admin.products.form.mediaDropTitle',
    defaultMessage: 'Drag & drop here',
  },
  formUploadFromComputer: {
    id: 'admin.products.form.uploadFromComputer',
    defaultMessage: 'Upload from computer',
  },
  formSelectFromLibrary: {
    id: 'admin.products.form.selectFromLibrary',
    defaultMessage: 'select from library',
  },
  formOr: { id: 'admin.products.form.or', defaultMessage: 'or' },
  formLibraryTitle: {
    id: 'admin.products.form.libraryTitle',
    defaultMessage: 'Media library',
  },
  formUploadFiles: { id: 'admin.products.form.uploadFiles', defaultMessage: 'Upload files' },
  formRemoveImage: { id: 'admin.products.form.removeImage', defaultMessage: 'Remove image' },
  formRemoveFile: { id: 'admin.products.form.removeFile', defaultMessage: 'Remove file' },
  formFeaturedImage: { id: 'admin.products.form.featuredImage', defaultMessage: 'Featured' },
  formImageActions: {
    id: 'admin.products.form.imageActions',
    defaultMessage: 'Image actions',
  },
  formSetFeatured: {
    id: 'admin.products.form.setFeatured',
    defaultMessage: 'Select as featured',
  },
  formRemoveFeatured: {
    id: 'admin.products.form.removeFeatured',
    defaultMessage: 'Remove as featured',
  },
  formMoveUpOrder: {
    id: 'admin.products.form.moveUpOrder',
    defaultMessage: 'Move up in order',
  },
  formMoveDownOrder: {
    id: 'admin.products.form.moveDownOrder',
    defaultMessage: 'Move down in order',
  },
  formRemoveFromSelection: {
    id: 'admin.products.form.removeFromSelection',
    defaultMessage: 'Remove from selection',
  },
  formUploading: { id: 'admin.products.form.uploading', defaultMessage: 'Uploading…' },
  formMediaCount: {
    id: 'admin.products.form.mediaCount',
    defaultMessage: '{count} of {max} media files added.',
  },
  formMediaHint: {
    id: 'admin.products.form.mediaHint',
    defaultMessage:
      'Supported file types: images (PNG, JPG, JPEG, WEBP, GIF). Max file size: {maxMb} MB.',
  },
  formDownloadableHint: {
    id: 'admin.products.form.downloadableHint',
    defaultMessage: 'Any file up to {maxMb} MB',
  },
  formErrorUnsupportedType: {
    id: 'admin.products.form.errorUnsupportedType',
    defaultMessage: 'Unsupported file type.',
  },
  formErrorFileTooLarge: {
    id: 'admin.products.form.errorFileTooLarge',
    defaultMessage: 'File exceeds the maximum size of {maxMb} MB.',
  },
  formErrorImageLimit: {
    id: 'admin.products.form.errorImageLimit',
    defaultMessage: 'You can add up to {max} images.',
  },
  formUploadFailed: {
    id: 'admin.products.form.uploadFailed',
    defaultMessage: 'Upload failed. Please try again.',
  },

  // Product form — information
  formInformation: {
    id: 'admin.products.form.information',
    defaultMessage: 'Product information',
  },
  formName: { id: 'admin.products.form.name', defaultMessage: 'Name' },
  formNamePlaceholder: {
    id: 'admin.products.form.namePlaceholder',
    defaultMessage: 'Product name',
  },
  formDescription: { id: 'admin.products.form.description', defaultMessage: 'Description' },
  formShortDescription: {
    id: 'admin.products.form.shortDescription',
    defaultMessage: 'Short description',
  },
  formStatus: { id: 'admin.products.form.status', defaultMessage: 'Status' },
  formStatusPublished: {
    id: 'admin.products.form.statusPublished',
    // Matches the list's Visible/Draft/Hidden vocabulary (see productsStatusPublished).
    defaultMessage: 'Visible',
  },
  formStatusDraft: { id: 'admin.products.form.statusDraft', defaultMessage: 'Draft' },
  formOptional: { id: 'admin.products.form.optional', defaultMessage: 'Optional' },

  // Product form — variants
  formVariants: { id: 'admin.products.form.variants', defaultMessage: 'Variants' },
  formVariantsWithCount: {
    id: 'admin.products.form.variantsWithCount',
    defaultMessage: 'Variants ({count})',
  },
  formVariantsDescription: {
    id: 'admin.products.form.variantsDescription',
    defaultMessage:
      'Add options to your product, such as colour, size, or material. Each combination of options creates a variant, which can have its own price, SKU, and inventory.',
  },
  formAddOption: { id: 'admin.products.form.addOption', defaultMessage: 'Add option' },
  formEditOption: { id: 'admin.products.form.editOption', defaultMessage: 'Edit option' },
  formOptionName: { id: 'admin.products.form.optionName', defaultMessage: 'Option name' },
  formOptionNamePlaceholder: {
    id: 'admin.products.form.optionNamePlaceholder',
    defaultMessage: 'e.g. colour, size',
  },
  formOptionType: { id: 'admin.products.form.optionType', defaultMessage: 'Type' },
  formOptionTypePlaceholder: {
    id: 'admin.products.form.optionTypePlaceholder',
    defaultMessage: 'e.g. blue, red, small, large, wood, clay',
  },
  formRemoveValue: { id: 'admin.products.form.removeValue', defaultMessage: 'Remove value' },
  formDeleteVariantTitle: {
    id: 'admin.products.form.deleteVariantTitle',
    defaultMessage: 'Delete option',
  },
  formDeleteVariantMessage: {
    id: 'admin.products.form.deleteVariantMessage',
    defaultMessage: 'Delete the “{name}” option? This can’t be undone.',
  },
  formVariantNameRequired: {
    id: 'admin.products.form.variantNameRequired',
    defaultMessage: 'Option name is required.',
  },
  formVariantNameDuplicate: {
    id: 'admin.products.form.variantNameDuplicate',
    defaultMessage: 'An option with this name already exists.',
  },
  formVariantValuesRequired: {
    id: 'admin.products.form.variantValuesRequired',
    defaultMessage: 'Add at least one value.',
  },

  // Product form — Simple/Variable selector + per-variant table
  formConfiguration: {
    id: 'admin.products.form.configuration',
    defaultMessage: 'Product configuration',
  },
  formKindSimple: { id: 'admin.products.form.kindSimple', defaultMessage: 'Simple product' },
  formKindSimpleDescription: {
    id: 'admin.products.form.kindSimpleDescription',
    defaultMessage: 'A single price, SKU, and stock level.',
  },
  formKindVariant: { id: 'admin.products.form.kindVariant', defaultMessage: 'Variable product' },
  formKindVariantDescription: {
    id: 'admin.products.form.kindVariantDescription',
    defaultMessage: 'Multiple variants, each with its own price, SKU, and stock.',
  },
  formVariantTableTitle: {
    id: 'admin.products.form.variantTableTitle',
    defaultMessage: 'Variants',
  },
  formVariantColVariant: {
    id: 'admin.products.form.variantColVariant',
    defaultMessage: 'Variants',
  },
  formVariantColPrice: { id: 'admin.products.form.variantColPrice', defaultMessage: 'Price' },
  formVariantColSalePrice: {
    id: 'admin.products.form.variantColSalePrice',
    defaultMessage: 'Sales price',
  },
  formVariantsGeneratedCount: {
    id: 'admin.products.form.variantsGeneratedCount',
    defaultMessage: '{count} variants generated from your options',
  },
  formVariantImageNeedsGallery: {
    id: 'admin.products.form.variantImageNeedsGallery',
    defaultMessage: 'Add product images first',
  },
  formVariantStockDecrease: {
    id: 'admin.products.form.variantStockDecrease',
    defaultMessage: 'Decrease stock',
  },
  formVariantStockIncrease: {
    id: 'admin.products.form.variantStockIncrease',
    defaultMessage: 'Increase stock',
  },
  formVariantColStock: { id: 'admin.products.form.variantColStock', defaultMessage: 'Stock' },
  formVariantColSku: { id: 'admin.products.form.variantColSku', defaultMessage: 'SKU' },
  formVariantColImage: { id: 'admin.products.form.variantColImage', defaultMessage: 'Image' },
  formVariantColWeight: {
    id: 'admin.products.form.variantColWeight',
    defaultMessage: 'Weight',
  },
  formVariantColActions: {
    id: 'admin.products.form.variantColActions',
    defaultMessage: 'Actions',
  },
  formVariantEmpty: {
    id: 'admin.products.form.variantEmpty',
    defaultMessage: 'Add options above to generate variants.',
  },
  formVariantUnlimitedToggle: {
    id: 'admin.products.form.variantUnlimitedToggle',
    defaultMessage: 'Unlimited stock',
  },
  formVariantSetImage: {
    id: 'admin.products.form.variantSetImage',
    defaultMessage: 'Set image',
  },
  formVariantChangeImage: {
    id: 'admin.products.form.variantChangeImage',
    defaultMessage: 'Change variant image',
  },
  formVariantRetire: {
    id: 'admin.products.form.variantRetire',
    defaultMessage: 'Delete variant',
  },
  formVariantRetiredBadge: {
    id: 'admin.products.form.variantRetiredBadge',
    defaultMessage: 'Deleted',
  },
  formVariantRetireTitle: {
    id: 'admin.products.form.variantRetireTitle',
    defaultMessage: 'Delete “{name}”?',
  },
  formVariantRetireMessage: {
    id: 'admin.products.form.variantRetireMessage',
    defaultMessage:
      'This variant is removed — along with its price, stock and SKU — when you save. You can undo it before then; once saved it can’t be recovered, and recreating the combination later makes a new variant with no history.',
  },
  formVariantRetireConfirm: {
    id: 'admin.products.form.variantRetireConfirm',
    defaultMessage: 'Delete',
  },
  formVariantUndo: {
    id: 'admin.products.form.variantUndo',
    defaultMessage: 'Undo',
  },
  formVariantRetireLastVariant: {
    id: 'admin.products.form.variantRetireLastVariant',
    defaultMessage: 'A product needs at least one variant.',
  },
  formVariantDeleteUnsaved: {
    id: 'admin.products.form.variantDeleteUnsaved',
    defaultMessage: 'Combinations are defined by your options until you save.',
  },
  formVariantUnsavedTag: {
    id: 'admin.products.form.variantUnsavedTag',
    defaultMessage: 'Unsaved',
  },
  formVariantSkuDuplicate: {
    id: 'admin.products.form.variantSkuDuplicate',
    defaultMessage: 'Another variant already uses this SKU.',
  },
  formVariantBulkFillLabel: {
    id: 'admin.products.form.variantBulkFillLabel',
    defaultMessage: 'Set all variants',
  },
  formVariantBulkApply: {
    id: 'admin.products.form.variantBulkApply',
    defaultMessage: 'Apply to all',
  },
  formVariantBulkPriceAria: {
    id: 'admin.products.form.variantBulkPriceAria',
    defaultMessage: 'Set price for all variants',
  },
  formVariantBulkSalePriceAria: {
    id: 'admin.products.form.variantBulkSalePriceAria',
    defaultMessage: 'Set sale price for all variants',
  },
  formVariantBulkStockAria: {
    id: 'admin.products.form.variantBulkStockAria',
    defaultMessage: 'Set stock for all variants',
  },
  formVariantBulkStockInvalid: {
    id: 'admin.products.form.variantBulkStockInvalid',
    defaultMessage: 'Stock must be a whole number of 0 or more.',
  },
  formVariantCombinationCount: {
    id: 'admin.products.form.variantCombinationCount',
    defaultMessage: '{count} of {max} combinations',
  },
  formVariantTooMany: {
    id: 'admin.products.form.variantTooMany',
    defaultMessage:
      'Too many variants: {count} combinations (maximum {max}). Remove some option values.',
  },
  formVariantDowngradeTitle: {
    id: 'admin.products.form.variantDowngradeTitle',
    defaultMessage: 'Switch to a simple product?',
  },
  formVariantDowngradeMessage: {
    id: 'admin.products.form.variantDowngradeMessage',
    defaultMessage:
      'This removes all {count} variants and their individual prices, SKUs, and stock. This can’t be undone.',
  },
  formVariantDowngradeConfirm: {
    id: 'admin.products.form.variantDowngradeConfirm',
    defaultMessage: 'Switch to simple',
  },
  formDigitalHasVariantsError: {
    id: 'admin.products.form.digitalHasVariantsError',
    defaultMessage: 'Remove all variants before switching to a digital product.',
  },
  formVariantValueLossTitle: {
    id: 'admin.products.form.variantValueLossTitle',
    defaultMessage: 'Delete the variants using these values?',
  },
  formVariantValueLossMessage: {
    id: 'admin.products.form.variantValueLossMessage',
    defaultMessage:
      'Removing {values} deletes every variant built on it ({count} in total), along with its price, stock and SKU. This can’t be undone.',
  },
  formVariantValueLossConfirm: {
    id: 'admin.products.form.variantValueLossConfirm',
    defaultMessage: 'Remove values',
  },
  formVariantValueRename: {
    id: 'admin.products.form.variantValueRename',
    defaultMessage: 'Rename {value}',
  },
  formVariantValueRemove: {
    id: 'admin.products.form.variantValueRemove',
    defaultMessage: 'Remove {value}',
  },
  formVariantValueDuplicate: {
    id: 'admin.products.form.variantValueDuplicate',
    defaultMessage: '"{values}" already exists.',
  },
  formVariantRowErrors: {
    id: 'admin.products.form.variantRowErrors',
    defaultMessage: 'Fix the highlighted variant errors before saving.',
  },
  formVariantRowPriceRequired: {
    id: 'admin.products.form.variantRowPriceRequired',
    defaultMessage: 'Price is required.',
  },
  formVariantRowPriceInvalid: {
    id: 'admin.products.form.variantRowPriceInvalid',
    defaultMessage: 'Enter a valid price of 0 or more.',
  },
  formVariantRowSaleInvalid: {
    id: 'admin.products.form.variantRowSaleInvalid',
    defaultMessage: 'Enter a valid sale price of 0 or more.',
  },
  formVariantRowSaleTooHigh: {
    id: 'admin.products.form.variantRowSaleTooHigh',
    defaultMessage: 'Sale price must be lower than the price.',
  },
  formVariantRowStockInvalid: {
    id: 'admin.products.form.variantRowStockInvalid',
    defaultMessage: 'Enter a whole number of 0 or more.',
  },
  formVariantMaxOptions: {
    id: 'admin.products.form.variantMaxOptions',
    defaultMessage: 'You can add up to {max} options.',
  },
  formVariantMaxValues: {
    id: 'admin.products.form.variantMaxValues',
    defaultMessage: 'You can add up to {max} values per option.',
  },
  formStatusHidden: {
    id: 'admin.products.form.statusHidden',
    defaultMessage: 'Hidden',
  },

  // Product form — pricing & stock
  formPricingAndStock: {
    id: 'admin.products.form.pricingAndStock',
    defaultMessage: 'Pricing and stock',
  },
  formRegularPrice: { id: 'admin.products.form.regularPrice', defaultMessage: 'Regular price' },
  formSalePrice: { id: 'admin.products.form.salePrice', defaultMessage: 'Sale price' },
  formSku: { id: 'admin.products.form.sku', defaultMessage: 'SKU' },
  formSkuTooltip: {
    id: 'admin.products.form.skuTooltip',
    defaultMessage:
      'Stock Keeping Unit — a unique code for this product. Leave it empty to generate one automatically.',
  },
  formSkuTooltipAria: {
    id: 'admin.products.form.skuTooltipAria',
    defaultMessage: 'About SKU',
  },
  formWeight: { id: 'admin.products.form.weight', defaultMessage: 'Weight' },
  formWeightUnit: { id: 'admin.products.form.weightUnit', defaultMessage: 'kg' },
  formStock: { id: 'admin.products.form.stock', defaultMessage: 'Stock' },
  formSoldIndividually: {
    id: 'admin.products.form.soldIndividually',
    defaultMessage: 'Only allow one per order',
  },
  formUnlimitedStock: {
    id: 'admin.products.form.unlimitedStock',
    defaultMessage: 'Unlimited stock',
  },
  formDecreaseStock: {
    id: 'admin.products.form.decreaseStock',
    defaultMessage: 'Decrease stock',
  },
  formIncreaseStock: {
    id: 'admin.products.form.increaseStock',
    defaultMessage: 'Increase stock',
  },
  formNameRequired: {
    id: 'admin.products.form.nameRequired',
    defaultMessage: 'Product name is required.',
  },
  formRegularPriceInvalid: {
    id: 'admin.products.form.regularPriceInvalid',
    defaultMessage: 'Regular price must be a valid number.',
  },
  formSalePriceInvalid: {
    id: 'admin.products.form.salePriceInvalid',
    defaultMessage: 'Sale price must be a valid number.',
  },
  formSalePriceTooHigh: {
    id: 'admin.products.form.salePriceTooHigh',
    defaultMessage: 'Sale price must be lower than the regular price.',
  },

  // Product form — organisation
  formOrganisation: {
    id: 'admin.products.form.organisation',
    defaultMessage: 'Product organisation',
  },
  formCategories: { id: 'admin.products.form.categories', defaultMessage: 'Category' },
  formCategoriesWithCount: {
    id: 'admin.products.form.categoriesWithCount',
    defaultMessage: 'Category ({count})',
  },
  formCategoryDescription: {
    id: 'admin.products.form.categoryDescription',
    defaultMessage: 'Select which category your product should appear in.',
  },
  formCreateCategory: {
    id: 'admin.products.form.createCategory',
    defaultMessage: 'Create category',
  },
  formRelatedProducts: {
    id: 'admin.products.form.relatedProducts',
    defaultMessage: 'Related products',
  },
  formRelatedProductsWithCount: {
    id: 'admin.products.form.relatedProductsWithCount',
    defaultMessage: 'Related products ({count})',
  },
  formRelatedDescription: {
    id: 'admin.products.form.relatedDescription',
    defaultMessage:
      'Add related or complementary products to increase cross-selling opportunities',
  },
  formRelatedAuto: {
    id: 'admin.products.form.relatedAuto',
    defaultMessage: 'Automatically select related products',
  },
  formRelatedAutoDescription: {
    id: 'admin.products.form.relatedAutoDescription',
    defaultMessage: 'Aida will pick related products for your product.',
  },
  formRelatedManual: {
    id: 'admin.products.form.relatedManual',
    defaultMessage: 'Choose related products manually',
  },
  formRelatedNone: {
    id: 'admin.products.form.relatedNone',
    defaultMessage: "Don't show related products",
  },
  formRelatedNoProducts: {
    id: 'admin.products.form.relatedNoProducts',
    defaultMessage: 'No products yet.',
  },

  // Product form — list picker (categories / related products)
  formPickerAll: { id: 'admin.products.form.pickerAll', defaultMessage: 'All' },
  formPickerSelected: {
    id: 'admin.products.form.pickerSelected',
    defaultMessage: 'Selected',
  },
  formPickerUnselected: {
    id: 'admin.products.form.pickerUnselected',
    defaultMessage: 'Unselected',
  },
  formPickerClear: { id: 'admin.products.form.pickerClear', defaultMessage: 'Clear all' },
  formPickerNoResults: {
    id: 'admin.products.form.pickerNoResults',
    defaultMessage: 'No results',
  },
  formPickerNoResultsDescription: {
    id: 'admin.products.form.pickerNoResultsDescription',
    defaultMessage: 'No results found. Try another filter.',
  },
  formPickerExpand: { id: 'admin.products.form.pickerExpand', defaultMessage: 'Expand' },
  formPickerCollapse: {
    id: 'admin.products.form.pickerCollapse',
    defaultMessage: 'Collapse',
  },

  // Categories — list
  categoriesTitle: { id: 'admin.categories.title', defaultMessage: 'Categories' },
  categoriesAdd: { id: 'admin.categories.add', defaultMessage: 'Add category' },
  categoriesSearchPlaceholder: {
    id: 'admin.categories.searchPlaceholder',
    defaultMessage: 'Search categories',
  },
  categoriesSearchLabel: {
    id: 'admin.categories.searchLabel',
    defaultMessage: 'Search categories',
  },
  categoriesLoading: {
    id: 'admin.categories.loading',
    defaultMessage: 'Loading categories…',
  },
  categoriesEmptyTitle: {
    id: 'admin.categories.emptyTitle',
    defaultMessage: 'No categories yet',
  },
  categoriesEmptyDescription: {
    id: 'admin.categories.emptyDescription',
    defaultMessage: 'Organise your products by adding your first category.',
  },
  categoriesNoResultsTitle: {
    id: 'admin.categories.noResultsTitle',
    defaultMessage: 'No results',
  },
  categoriesNoResultsDescription: {
    id: 'admin.categories.noResultsDescription',
    defaultMessage: 'Try another search term.',
  },
  categoriesErrorTitle: {
    id: 'admin.categories.errorTitle',
    defaultMessage: 'Couldn’t load categories',
  },
  categoriesErrorDescription: {
    id: 'admin.categories.errorDescription',
    defaultMessage: 'We couldn’t load your categories. Please try again.',
  },
  categoriesColImage: { id: 'admin.categories.colImage', defaultMessage: 'Image' },
  categoriesColName: { id: 'admin.categories.colName', defaultMessage: 'Name' },
  categoriesColDescription: {
    id: 'admin.categories.colDescription',
    defaultMessage: 'Description',
  },
  categoriesColSlug: { id: 'admin.categories.colSlug', defaultMessage: 'Slug' },
  categoriesColCount: { id: 'admin.categories.colCount', defaultMessage: 'Products' },
  categoriesActions: {
    id: 'admin.categories.actions',
    defaultMessage: 'Category actions',
  },
  categoriesEdit: { id: 'admin.categories.edit', defaultMessage: 'Edit' },
  categoriesDelete: { id: 'admin.categories.delete', defaultMessage: 'Delete' },
  categoriesCancel: { id: 'admin.categories.cancel', defaultMessage: 'Cancel' },
  categoriesDeleteTitle: {
    id: 'admin.categories.deleteTitle',
    defaultMessage: 'Delete category',
  },
  categoriesDeleteMessage: {
    id: 'admin.categories.deleteMessage',
    defaultMessage:
      'Are you sure you want to delete “{name}”? This action cannot be undone.',
  },
  categoriesDeleteLoading: {
    id: 'admin.categories.deleteLoading',
    defaultMessage: 'Deleting…',
  },
  categoriesDeleteError: {
    id: 'admin.categories.deleteError',
    defaultMessage: 'Couldn’t delete the category. Please try again.',
  },
  categoriesDeleteHasChildren: {
    id: 'admin.categories.deleteHasChildren',
    defaultMessage:
      'Move or delete this category’s subcategories before deleting it.',
  },

  // Categories — form
  categoriesFormAddTitle: {
    id: 'admin.categories.form.addTitle',
    defaultMessage: 'Add new category',
  },
  categoriesFormEditTitle: {
    id: 'admin.categories.form.editTitle',
    defaultMessage: 'Edit category',
  },
  categoriesFormInformation: {
    id: 'admin.categories.form.information',
    defaultMessage: 'Category information',
  },
  categoriesFormName: { id: 'admin.categories.form.name', defaultMessage: 'Name' },
  categoriesFormNamePlaceholder: {
    id: 'admin.categories.form.namePlaceholder',
    defaultMessage: 'Category name',
  },
  categoriesFormSlug: { id: 'admin.categories.form.slug', defaultMessage: 'Slug' },
  categoriesFormSlugHint: {
    id: 'admin.categories.form.slugHint',
    defaultMessage:
      'A slug is the part of a URL that identifies a specific category. Leave it empty and the system will automatically generate one.',
  },
  categoriesFormParent: {
    id: 'admin.categories.form.parent',
    defaultMessage: 'Parent category',
  },
  categoriesFormParentNone: {
    id: 'admin.categories.form.parentNone',
    defaultMessage: 'None (top-level)',
  },
  categoriesFormDescription: {
    id: 'admin.categories.form.description',
    defaultMessage: 'Description',
  },
  categoriesFormSave: {
    id: 'admin.categories.form.save',
    defaultMessage: 'Save changes',
  },
  categoriesFormCreate: {
    id: 'admin.categories.form.create',
    defaultMessage: 'Create category',
  },
  categoriesFormSaving: { id: 'admin.categories.form.saving', defaultMessage: 'Saving…' },
  categoriesFormLoading: {
    id: 'admin.categories.form.loading',
    defaultMessage: 'Loading category…',
  },
  categoriesFormLoadErrorTitle: {
    id: 'admin.categories.form.loadErrorTitle',
    defaultMessage: 'Couldn’t load category',
  },
  categoriesFormLoadErrorDescription: {
    id: 'admin.categories.form.loadErrorDescription',
    defaultMessage: 'We couldn’t load this category. Please go back and try again.',
  },
  categoriesFormSaveError: {
    id: 'admin.categories.form.saveError',
    defaultMessage: 'Couldn’t save the category. Please try again.',
  },
  categoriesNameRequired: {
    id: 'admin.categories.form.nameRequired',
    defaultMessage: 'Category name is required.',
  },
  categoriesParentInvalid: {
    id: 'admin.categories.form.parentInvalid',
    defaultMessage: 'A category can’t be its own parent.',
  },
  categoriesSlugConflict: {
    id: 'admin.categories.form.slugConflict',
    defaultMessage: 'That slug is already in use. Please choose another.',
  },

  ordersTitle: { id: 'admin.orders.title', defaultMessage: 'Orders' },
  ordersLoading: { id: 'admin.orders.loading', defaultMessage: 'Loading orders…' },
  ordersSearchPlaceholder: {
    id: 'admin.orders.searchPlaceholder',
    defaultMessage: 'Search orders',
  },
  ordersSearchLabel: { id: 'admin.orders.searchLabel', defaultMessage: 'Search orders' },
  ordersResetStatusFilter: {
    id: 'admin.orders.resetStatusFilter',
    defaultMessage: 'Reset status filter',
  },
  ordersEmptyTitle: { id: 'admin.orders.emptyTitle', defaultMessage: 'No orders yet' },
  ordersEmptyDescription: {
    id: 'admin.orders.emptyDescription',
    defaultMessage: 'Orders placed in your shop will appear here.',
  },
  ordersNoResultsTitle: {
    id: 'admin.orders.noResultsTitle',
    defaultMessage: 'No matching orders',
  },
  ordersNoResultsDescription: {
    id: 'admin.orders.noResultsDescription',
    defaultMessage: 'Try a different search term or status filter.',
  },
  ordersErrorTitle: { id: 'admin.orders.errorTitle', defaultMessage: 'Couldn’t load orders' },
  ordersErrorDescription: {
    id: 'admin.orders.errorDescription',
    defaultMessage: 'Something went wrong loading orders. Please try again.',
  },
  ordersRetry: { id: 'admin.orders.retry', defaultMessage: 'Retry' },

  ordersColNumber: { id: 'admin.orders.colNumber', defaultMessage: 'Order' },
  ordersColCustomer: { id: 'admin.orders.colCustomer', defaultMessage: 'Customer' },
  ordersColStatus: { id: 'admin.orders.colStatus', defaultMessage: 'Status' },
  ordersColDate: { id: 'admin.orders.colDate', defaultMessage: 'Date' },
  ordersColPayment: { id: 'admin.orders.colPayment', defaultMessage: 'Payment method' },
  ordersColTotal: { id: 'admin.orders.colTotal', defaultMessage: 'Total' },
  ordersActions: { id: 'admin.orders.actions', defaultMessage: 'Order actions' },
  ordersView: { id: 'admin.orders.view', defaultMessage: 'View order {number}' },
  ordersClose: { id: 'admin.orders.close', defaultMessage: 'Close' },
  ordersMenuView: { id: 'admin.orders.menu.view', defaultMessage: 'View' },
  ordersMenuCancel: { id: 'admin.orders.menu.cancel', defaultMessage: 'Cancel order' },
  ordersMenuRefund: { id: 'admin.orders.menu.refund', defaultMessage: 'Refund' },
  ordersMenuDelete: { id: 'admin.orders.menu.delete', defaultMessage: 'Delete' },
  ordersMarkPaid: { id: 'admin.orders.markPaid', defaultMessage: 'Mark as paid' },
  ordersFulfil: { id: 'admin.orders.fulfil', defaultMessage: 'Fulfil' },
  ordersCancel: { id: 'admin.orders.cancel', defaultMessage: 'Cancel' },

  // Mark-as-paid dialog
  ordersMarkPaidTitle: { id: 'admin.orders.markPaidTitle', defaultMessage: 'Mark order as paid' },
  ordersMarkPaidMessage: {
    id: 'admin.orders.markPaidMessage',
    defaultMessage: 'Record the full balance as paid for this order?',
  },
  ordersMarkPaidLoading: { id: 'admin.orders.markPaidLoading', defaultMessage: 'Marking…' },

  // Fulfil dialog
  ordersFulfilTitle: { id: 'admin.orders.fulfilTitle', defaultMessage: 'Fulfil order' },
  ordersFulfilMessage: {
    id: 'admin.orders.fulfilMessage',
    defaultMessage: 'Mark every shippable item in this order as shipped?',
  },
  ordersFulfilLoading: { id: 'admin.orders.fulfilLoading', defaultMessage: 'Fulfilling…' },

  // Delete-order dialog
  ordersDeleteTitle: { id: 'admin.orders.deleteTitle', defaultMessage: 'Delete order' },
  ordersDeleteMessage: {
    id: 'admin.orders.deleteMessage',
    defaultMessage: 'Are you sure you want to delete this order? This action cannot be undone.',
  },
  ordersDeleteConfirm: { id: 'admin.orders.deleteConfirm', defaultMessage: 'Delete' },
  ordersDeleteLoading: { id: 'admin.orders.deleteLoading', defaultMessage: 'Deleting…' },

  // Cancel-order dialog
  ordersCancelTitle: { id: 'admin.orders.cancelTitle', defaultMessage: 'Cancel order' },
  ordersCancelReasonLabel: {
    id: 'admin.orders.cancelReasonLabel',
    defaultMessage: 'Reason (optional)',
  },
  ordersCancelReasonPlaceholder: {
    id: 'admin.orders.cancelReasonPlaceholder',
    defaultMessage: 'Add a reason for cancelling this order',
  },
  ordersCancelConfirm: { id: 'admin.orders.cancelConfirm', defaultMessage: 'Cancel order' },
  ordersCancelDismiss: { id: 'admin.orders.cancelDismiss', defaultMessage: 'Keep order' },
  ordersCancelLoading: { id: 'admin.orders.cancelLoading', defaultMessage: 'Cancelling…' },

  // Edit-status dialog
  ordersEditStatus: { id: 'admin.orders.editStatus', defaultMessage: 'Edit status' },
  ordersEditStatusTitle: {
    id: 'admin.orders.editStatusTitle',
    defaultMessage: 'Edit order status',
  },
  ordersEditStatusLabel: { id: 'admin.orders.editStatusLabel', defaultMessage: 'Status' },
  ordersEditStatusReasonLabel: {
    id: 'admin.orders.editStatusReasonLabel',
    defaultMessage: 'Reason (optional)',
  },
  ordersEditStatusReasonPlaceholder: {
    id: 'admin.orders.editStatusReasonPlaceholder',
    defaultMessage: 'Add a reason for this status change',
  },
  ordersEditStatusNoMoneyWarning: {
    id: 'admin.orders.editStatusNoMoneyWarning',
    defaultMessage:
      'This only changes the order status — no payment is refunded or voided. Use Refund or Cancel order to move money.',
  },
  ordersEditStatusConfirm: { id: 'admin.orders.editStatusConfirm', defaultMessage: 'Save' },
  ordersEditStatusLoading: {
    id: 'admin.orders.editStatusLoading',
    defaultMessage: 'Saving…',
  },

  // Refund-order dialog
  ordersRefundTitle: { id: 'admin.orders.refundTitle', defaultMessage: 'Refund order' },
  ordersRefundAmountLabel: {
    id: 'admin.orders.refundAmountLabel',
    defaultMessage: 'Refund amount',
  },
  ordersRefundReasonLabel: {
    id: 'admin.orders.refundReasonLabel',
    defaultMessage: 'Reason (optional)',
  },
  ordersRefundReasonPlaceholder: {
    id: 'admin.orders.refundReasonPlaceholder',
    defaultMessage: 'Add a reason for this refund',
  },
  ordersRefundRestockLabel: {
    id: 'admin.orders.refundRestockLabel',
    defaultMessage: 'Restock the refunded items',
  },
  ordersRefundConfirm: { id: 'admin.orders.refundConfirm', defaultMessage: 'Refund' },
  ordersRefundLoading: { id: 'admin.orders.refundLoading', defaultMessage: 'Refunding…' },

  // Order action toasts + error remap
  ordersToastCancelled: { id: 'admin.orders.toastCancelled', defaultMessage: 'Order cancelled' },
  ordersToastRefunded: { id: 'admin.orders.toastRefunded', defaultMessage: 'Order refunded' },
  ordersToastDeleted: { id: 'admin.orders.toastDeleted', defaultMessage: 'Order deleted' },
  ordersToastMarkedPaid: { id: 'admin.orders.toastMarkedPaid', defaultMessage: 'Order marked as paid' },
  ordersToastFulfilled: { id: 'admin.orders.toastFulfilled', defaultMessage: 'Order fulfilled' },
  ordersToastStatusUpdated: {
    id: 'admin.orders.toastStatusUpdated',
    defaultMessage: 'Order status updated',
  },
  ordersActionError: {
    id: 'admin.orders.actionError',
    defaultMessage: 'Something went wrong. Please try again.',
  },
  ordersErrorInvalidTransition: {
    id: 'admin.orders.errorInvalidTransition',
    defaultMessage: 'This order can’t be changed in its current state.',
  },
  ordersErrorNotEditable: {
    id: 'admin.orders.errorNotEditable',
    defaultMessage: 'This order can no longer be edited.',
  },
  ordersErrorRefundInProgress: {
    id: 'admin.orders.errorRefundInProgress',
    defaultMessage: 'A refund for this order is already in progress.',
  },
  ordersErrorNotDeletable: {
    id: 'admin.orders.errorNotDeletable',
    defaultMessage: 'Only cancelled or completed orders can be deleted.',
  },
  ordersErrorAlreadyPaid: {
    id: 'admin.orders.errorAlreadyPaid',
    defaultMessage: 'This order has already been paid.',
  },
  ordersErrorQtyExceeded: {
    id: 'admin.orders.errorQtyExceeded',
    defaultMessage: 'Some items have already been fulfilled.',
  },
  ordersErrorPaymentUnavailable: {
    id: 'admin.orders.errorPaymentUnavailable',
    defaultMessage: 'The payment service is unavailable. Please try again later.',
  },

  ordersStatusPending: { id: 'admin.orders.status.pending', defaultMessage: 'Pending' },
  ordersStatusProcessing: {
    id: 'admin.orders.status.processing',
    defaultMessage: 'Processing',
  },
  ordersStatusOnHold: { id: 'admin.orders.status.onHold', defaultMessage: 'On hold' },
  ordersStatusCompleted: {
    id: 'admin.orders.status.completed',
    defaultMessage: 'Completed',
  },
  ordersStatusCancelled: {
    id: 'admin.orders.status.cancelled',
    defaultMessage: 'Cancelled',
  },
  ordersStatusRefunded: { id: 'admin.orders.status.refunded', defaultMessage: 'Refunded' },
  ordersStatusFailed: { id: 'admin.orders.status.failed', defaultMessage: 'Failed' },

  ordersItemsCount: {
    id: 'admin.orders.itemsCount',
    defaultMessage: '{count, plural, one {# item} other {# items}}',
  },
  ordersDrawerOrderId: {
    id: 'admin.orders.drawer.orderId',
    defaultMessage: 'Order ID',
  },
  ordersDrawerPayment: {
    id: 'admin.orders.drawer.payment',
    defaultMessage: 'Payment',
  },
  ordersDrawerItems: { id: 'admin.orders.drawer.items', defaultMessage: 'Items' },
  ordersDrawerQuantity: { id: 'admin.orders.drawer.quantity', defaultMessage: '×{quantity}' },
  ordersDrawerDiscount: { id: 'admin.orders.drawer.discount', defaultMessage: 'Discount' },
  ordersDrawerShipping: { id: 'admin.orders.drawer.shipping', defaultMessage: 'Shipping' },
  ordersDrawerTax: { id: 'admin.orders.drawer.tax', defaultMessage: 'Tax' },
  ordersDrawerTotal: { id: 'admin.orders.drawer.total', defaultMessage: 'Total' },
  ordersDrawerCoupons: { id: 'admin.orders.drawer.coupons', defaultMessage: 'Coupons' },
  ordersDrawerBilling: {
    id: 'admin.orders.drawer.billing',
    defaultMessage: 'Billing',
  },
  ordersDrawerShippingAddress: {
    id: 'admin.orders.drawer.shippingAddress',
    defaultMessage: 'Shipping',
  },

  // Coupons — list
  couponsTitle: { id: 'admin.coupons.title', defaultMessage: 'Coupons' },
  couponsAdd: { id: 'admin.coupons.add', defaultMessage: 'Add coupon' },
  couponsSearchPlaceholder: {
    id: 'admin.coupons.searchPlaceholder',
    defaultMessage: 'Search coupons',
  },
  couponsSearchLabel: { id: 'admin.coupons.searchLabel', defaultMessage: 'Search coupons' },
  couponsLoading: { id: 'admin.coupons.loading', defaultMessage: 'Loading coupons…' },
  couponsEmptyTitle: { id: 'admin.coupons.emptyTitle', defaultMessage: 'No coupons yet' },
  couponsEmptyDescription: {
    id: 'admin.coupons.emptyDescription',
    defaultMessage: 'Offer discounts by creating your first coupon.',
  },
  couponsNoResultsTitle: {
    id: 'admin.coupons.noResultsTitle',
    defaultMessage: 'No results',
  },
  couponsNoResultsDescription: {
    id: 'admin.coupons.noResultsDescription',
    defaultMessage: 'Try another search term.',
  },
  couponsErrorTitle: {
    id: 'admin.coupons.errorTitle',
    defaultMessage: 'Couldn’t load coupons',
  },
  couponsErrorDescription: {
    id: 'admin.coupons.errorDescription',
    defaultMessage: 'We couldn’t load your coupons. Please try again.',
  },
  couponsRetry: { id: 'admin.coupons.retry', defaultMessage: 'Retry' },

  couponsColCode: { id: 'admin.coupons.colCode', defaultMessage: 'Code' },
  couponsColType: { id: 'admin.coupons.colType', defaultMessage: 'Type' },
  couponsColAmount: { id: 'admin.coupons.colAmount', defaultMessage: 'Amount' },
  couponsColUsage: { id: 'admin.coupons.colUsage', defaultMessage: 'Usage' },
  couponsColExpires: { id: 'admin.coupons.colExpires', defaultMessage: 'Expires' },
  couponsColStatus: { id: 'admin.coupons.colStatus', defaultMessage: 'Status' },
  couponsActions: { id: 'admin.coupons.actions', defaultMessage: 'Coupon actions' },
  couponsEdit: { id: 'admin.coupons.edit', defaultMessage: 'Edit' },
  couponsDelete: { id: 'admin.coupons.delete', defaultMessage: 'Delete' },
  couponsCancel: { id: 'admin.coupons.cancel', defaultMessage: 'Cancel' },
  couponsDeleteTitle: { id: 'admin.coupons.deleteTitle', defaultMessage: 'Delete coupon' },
  couponsDeleteMessage: {
    id: 'admin.coupons.deleteMessage',
    defaultMessage:
      'Are you sure you want to delete “{code}”? This action cannot be undone.',
  },
  couponsDeleteLoading: { id: 'admin.coupons.deleteLoading', defaultMessage: 'Deleting…' },
  couponsDeleteError: {
    id: 'admin.coupons.deleteError',
    defaultMessage: 'Couldn’t delete the coupon. Please try again.',
  },

  // Coupons — discount types
  couponsTypePercent: { id: 'admin.coupons.typePercent', defaultMessage: 'Percentage' },
  couponsTypeFixedCart: { id: 'admin.coupons.typeFixedCart', defaultMessage: 'Fixed Cart' },
  couponsTypeFixedProduct: {
    id: 'admin.coupons.typeFixedProduct',
    defaultMessage: 'Fixed Product',
  },

  // Coupons — status pill
  couponsStatusActive: { id: 'admin.coupons.statusActive', defaultMessage: 'Active' },
  couponsStatusExpired: { id: 'admin.coupons.statusExpired', defaultMessage: 'Expired' },

  // Coupons — form
  couponsFormAddTitle: {
    id: 'admin.coupons.form.addTitle',
    defaultMessage: 'Add new coupon',
  },
  couponsFormEditTitle: {
    id: 'admin.coupons.form.editTitle',
    defaultMessage: 'Edit coupon',
  },
  couponsFormInformation: {
    id: 'admin.coupons.form.information',
    defaultMessage: 'Coupon information',
  },
  couponsFormLimitations: {
    id: 'admin.coupons.form.limitations',
    defaultMessage: 'Limitations',
  },
  couponsFormCode: { id: 'admin.coupons.form.code', defaultMessage: 'Coupon code' },
  couponsFormCodePlaceholder: {
    id: 'admin.coupons.form.codePlaceholder',
    defaultMessage: 'e.g. SUMMER20',
  },
  couponsFormType: { id: 'admin.coupons.form.type', defaultMessage: 'Type' },
  couponsFormAmount: { id: 'admin.coupons.form.amount', defaultMessage: 'Amount' },
  couponsFormDescription: {
    id: 'admin.coupons.form.description',
    defaultMessage: 'Description',
  },
  couponsFormExpires: { id: 'admin.coupons.form.expires', defaultMessage: 'Expires' },
  couponsFormUsageLimit: {
    id: 'admin.coupons.form.usageLimit',
    defaultMessage: 'Usage limit',
  },
  couponsFormUsageLimitPlaceholder: {
    id: 'admin.coupons.form.usageLimitPlaceholder',
    defaultMessage: 'Unlimited',
  },
  couponsFormMinimumAmount: {
    id: 'admin.coupons.form.minimumAmount',
    defaultMessage: 'Minimum cart amount',
  },
  couponsFormMaximumAmount: {
    id: 'admin.coupons.form.maximumAmount',
    defaultMessage: 'Maximum cart amount',
  },
  couponsFormIndividualUse: {
    id: 'admin.coupons.form.individualUse',
    defaultMessage: 'Individual use only',
  },
  couponsFormIndividualUseDesc: {
    id: 'admin.coupons.form.individualUseDesc',
    defaultMessage: 'Cannot be combined with other coupons',
  },
  couponsFormFreeShipping: {
    id: 'admin.coupons.form.freeShipping',
    defaultMessage: 'Free shipping',
  },
  couponsFormFreeShippingDesc: {
    id: 'admin.coupons.form.freeShippingDesc',
    defaultMessage: 'Waive shipping costs with this coupon',
  },
  couponsFormSave: { id: 'admin.coupons.form.save', defaultMessage: 'Save' },
  couponsFormCreate: { id: 'admin.coupons.form.create', defaultMessage: 'Create' },
  couponsFormSaving: { id: 'admin.coupons.form.saving', defaultMessage: 'Saving…' },
  couponsFormLoading: {
    id: 'admin.coupons.form.loading',
    defaultMessage: 'Loading coupon…',
  },
  couponsFormLoadErrorTitle: {
    id: 'admin.coupons.form.loadErrorTitle',
    defaultMessage: 'Couldn’t load coupon',
  },
  couponsFormLoadErrorDescription: {
    id: 'admin.coupons.form.loadErrorDescription',
    defaultMessage: 'We couldn’t load this coupon. Please go back and try again.',
  },
  couponsFormSaveError: {
    id: 'admin.coupons.form.saveError',
    defaultMessage: 'Couldn’t save the coupon. Please try again.',
  },

  // Coupons — validation
  couponsCodeRequired: {
    id: 'admin.coupons.form.codeRequired',
    defaultMessage: 'Coupon code is required.',
  },
  couponsCodeDuplicate: {
    id: 'admin.coupons.form.codeDuplicate',
    defaultMessage: 'A coupon with this code already exists.',
  },
  couponsAmountRequired: {
    id: 'admin.coupons.form.amountRequired',
    defaultMessage: 'Amount is required.',
  },
  couponsAmountInvalid: {
    id: 'admin.coupons.form.amountInvalid',
    defaultMessage: 'Amount must be a valid number of 0 or more.',
  },
  couponsAmountPercentTooHigh: {
    id: 'admin.coupons.form.amountPercentTooHigh',
    defaultMessage: 'A percentage discount can’t exceed 100%.',
  },
  couponsExpiryInPast: {
    id: 'admin.coupons.form.expiryInPast',
    defaultMessage: 'The expiry date can’t be in the past.',
  },
  couponsUsageLimitInvalid: {
    id: 'admin.coupons.form.usageLimitInvalid',
    defaultMessage: 'Usage limit must be a whole number of 0 or more.',
  },
  couponsMinimumAmountInvalid: {
    id: 'admin.coupons.form.minimumAmountInvalid',
    defaultMessage: 'Minimum cart amount must be 0 or more.',
  },
  couponsMaximumAmountInvalid: {
    id: 'admin.coupons.form.maximumAmountInvalid',
    defaultMessage: 'Maximum cart amount must be 0 or more.',
  },
  couponsExpiryInvalid: {
    id: 'admin.coupons.form.expiryInvalid',
    defaultMessage: 'The expiry date must be after the coupon’s start date.',
  },
  couponsSpendInvalid: {
    id: 'admin.coupons.form.spendInvalid',
    defaultMessage: 'The maximum cart amount must be greater than the minimum.',
  },

  // Shipping — screen
  shippingTitle: { id: 'admin.shipping.title', defaultMessage: 'Shipping' },
  shippingLoading: {
    id: 'admin.shipping.loading',
    defaultMessage: 'Loading shipping…',
  },
  shippingErrorTitle: {
    id: 'admin.shipping.errorTitle',
    defaultMessage: 'Couldn’t load shipping',
  },
  shippingErrorDescription: {
    id: 'admin.shipping.errorDescription',
    defaultMessage: 'We couldn’t load your shipping settings. Please try again.',
  },
  shippingRetry: { id: 'admin.shipping.retry', defaultMessage: 'Try again' },

  // Shipping — shared row controls
  shippingMoveUp: { id: 'admin.shipping.moveUp', defaultMessage: 'Move up' },
  shippingMoveDown: { id: 'admin.shipping.moveDown', defaultMessage: 'Move down' },
  shippingEdit: { id: 'admin.shipping.edit', defaultMessage: 'Edit' },
  shippingDelete: { id: 'admin.shipping.delete', defaultMessage: 'Delete' },
  shippingCancel: { id: 'admin.shipping.cancel', defaultMessage: 'Cancel' },
  shippingDeleteLoading: {
    id: 'admin.shipping.deleteLoading',
    defaultMessage: 'Deleting…',
  },
  shippingDescription: {
    id: 'admin.shipping.description',
    defaultMessage:
      'With these settings you can add shipping costs to your orders. Create regional differences if you ship over large distances.',
  },
  shippingAddOption: {
    id: 'admin.shipping.addOption',
    defaultMessage: 'Add option',
  },
  shippingFieldName: { id: 'admin.shipping.field.name', defaultMessage: 'Name' },
  shippingFieldType: { id: 'admin.shipping.field.type', defaultMessage: 'Type' },
  shippingFieldShipsTo: { id: 'admin.shipping.field.shipsTo', defaultMessage: 'Ships to' },
  shippingFieldPickupAddress: {
    id: 'admin.shipping.field.pickupAddress',
    defaultMessage: 'Pickup address',
  },
  shippingDragToReorder: {
    id: 'admin.shipping.dragToReorder',
    defaultMessage: 'Drag to reorder',
  },

  // Shipping — custom zones section
  shippingCustomTitle: {
    id: 'admin.shipping.custom.title',
    defaultMessage: 'Custom shipping',
  },
  shippingCustomDescription: {
    id: 'admin.shipping.custom.description',
    defaultMessage: 'Add shipping destinations and set the shipping price per order.',
  },
  shippingEverywhere: {
    id: 'admin.shipping.everywhere',
    defaultMessage: 'Everywhere',
  },
  shippingMethodFlatRate: {
    id: 'admin.shipping.method.flatRate',
    defaultMessage: 'Flat rate',
  },
  shippingMethodFreeShipping: {
    id: 'admin.shipping.method.freeShipping',
    defaultMessage: 'Free shipping',
  },
  shippingNoMethods: {
    id: 'admin.shipping.noMethods',
    defaultMessage: 'No shipping methods',
  },
  shippingDeleteZoneTitle: {
    id: 'admin.shipping.deleteZoneTitle',
    defaultMessage: 'Delete shipping zone',
  },
  shippingDeleteZoneMessage: {
    id: 'admin.shipping.deleteZoneMessage',
    defaultMessage: 'Delete the “{name}” zone? This can’t be undone.',
  },
  shippingDeleteZoneError: {
    id: 'admin.shipping.deleteZoneError',
    defaultMessage: 'Couldn’t delete the zone. Please try again.',
  },

  // Shipping — local pickup section
  shippingPickupTitle: {
    id: 'admin.shipping.pickup.title',
    defaultMessage: 'Local pickup',
  },
  shippingPickupDescription: {
    id: 'admin.shipping.pickup.description',
    defaultMessage:
      'Add a local pickup option for your customers to come pick up items at your location.',
  },
  shippingPickupUnnamed: {
    id: 'admin.shipping.pickup.unnamed',
    defaultMessage: 'Unnamed location',
  },
  shippingDeletePickupTitle: {
    id: 'admin.shipping.deletePickupTitle',
    defaultMessage: 'Delete pickup location',
  },
  shippingDeletePickupMessage: {
    id: 'admin.shipping.deletePickupMessage',
    defaultMessage: 'Delete the “{name}” pickup location? This can’t be undone.',
  },
  shippingDeletePickupError: {
    id: 'admin.shipping.deletePickupError',
    defaultMessage: 'Couldn’t delete the pickup location. Please try again.',
  },

  // Shipping — shared form controls
  shippingSaving: { id: 'admin.shipping.form.saving', defaultMessage: 'Saving…' },
  shippingDiscardTitle: {
    id: 'admin.shipping.form.discardTitle',
    defaultMessage: 'Discard changes?',
  },
  shippingDiscardMessage: {
    id: 'admin.shipping.form.discardMessage',
    defaultMessage: 'Your unsaved changes will be lost.',
  },
  shippingDiscard: { id: 'admin.shipping.form.discard', defaultMessage: 'Discard' },
  shippingKeepEditing: {
    id: 'admin.shipping.form.keepEditing',
    defaultMessage: 'Keep editing',
  },

  // Shipping — custom zone form
  shippingZoneAddTitle: {
    id: 'admin.shipping.zone.addTitle',
    defaultMessage: 'Add custom shipping',
  },
  shippingZoneEditTitle: {
    id: 'admin.shipping.zone.editTitle',
    defaultMessage: 'Edit custom shipping',
  },
  shippingZoneSave: { id: 'admin.shipping.zone.save', defaultMessage: 'Save' },
  shippingZoneName: { id: 'admin.shipping.zone.name', defaultMessage: 'Zone name' },
  shippingZoneNamePlaceholder: {
    id: 'admin.shipping.zone.namePlaceholder',
    defaultMessage: 'e.g. local, or worldwide',
  },
  shippingZoneNameRequired: {
    id: 'admin.shipping.zone.nameRequired',
    defaultMessage: 'Zone name is required.',
  },
  shippingZoneRegions: {
    id: 'admin.shipping.zone.regions',
    defaultMessage: 'Regions',
  },
  shippingZoneSectionTitle: {
    id: 'admin.shipping.zone.sectionTitle',
    defaultMessage: 'Shipping zone',
  },
  shippingZoneSectionDescription: {
    id: 'admin.shipping.zone.sectionDescription',
    defaultMessage:
      'A shipping zone consists of the region(s) you’d like to ship to and the shipping method(s) offered. A shopper can only be matched to one zone, and your shop will use their shipping address to show them the methods available in their area.',
  },
  shippingRegionsHelp: {
    id: 'admin.shipping.zone.regionsHelp',
    defaultMessage:
      'List the regions you’d like to include in your shipping zone. Customers will be matched against these regions.',
  },
  shippingFreeShippingDescription: {
    id: 'admin.shipping.freeShipping.description',
    defaultMessage:
      'Free shipping is a special method which can be triggered with coupons and minimum spends.',
  },
  shippingFlatRateDescription: {
    id: 'admin.shipping.flatRate.description',
    defaultMessage: 'Lets you charge a fixed rate for shipping.',
  },
  shippingZoneSaveError: {
    id: 'admin.shipping.zone.saveError',
    defaultMessage: 'Couldn’t save the zone. Please try again.',
  },
  shippingZoneLoadErrorTitle: {
    id: 'admin.shipping.zone.loadErrorTitle',
    defaultMessage: 'Couldn’t load zone',
  },
  shippingZoneLoadErrorDescription: {
    id: 'admin.shipping.zone.loadErrorDescription',
    defaultMessage: 'We couldn’t load this zone. Please go back and try again.',
  },

  // Shipping — region selector
  shippingRegionSearchLabel: {
    id: 'admin.shipping.region.searchLabel',
    defaultMessage: 'Search regions',
  },
  shippingRegionSearchPlaceholder: {
    id: 'admin.shipping.region.searchPlaceholder',
    defaultMessage: 'Search regions',
  },
  shippingRegionExpand: {
    id: 'admin.shipping.region.expand',
    defaultMessage: 'Expand',
  },
  shippingRegionCollapse: {
    id: 'admin.shipping.region.collapse',
    defaultMessage: 'Collapse',
  },
  shippingRegionNoResults: {
    id: 'admin.shipping.region.noResults',
    defaultMessage: 'No regions match your search.',
  },
  shippingRegionsSave: {
    id: 'admin.shipping.region.save',
    defaultMessage: 'Save',
  },

  // Shipping — methods (within the zone form)
  shippingMethodsTitle: {
    id: 'admin.shipping.methods.title',
    defaultMessage: 'Shipping methods',
  },
  shippingAddFlatRate: {
    id: 'admin.shipping.methods.addFlatRate',
    defaultMessage: 'Add flat rate',
  },
  shippingAddFreeShipping: {
    id: 'admin.shipping.methods.addFreeShipping',
    defaultMessage: 'Add free shipping',
  },
  shippingEditMethod: {
    id: 'admin.shipping.methods.edit',
    defaultMessage: 'Edit method',
  },
  shippingDeleteMethod: {
    id: 'admin.shipping.methods.delete',
    defaultMessage: 'Remove method',
  },
  shippingMethodCancel: {
    id: 'admin.shipping.methods.cancel',
    defaultMessage: 'Cancel',
  },
  shippingMethodSave: { id: 'admin.shipping.methods.save', defaultMessage: 'Save' },
  shippingMethodTitleLabel: {
    id: 'admin.shipping.methods.titleLabel',
    defaultMessage: 'Name',
  },
  shippingMethodTitleRequired: {
    id: 'admin.shipping.methods.titleRequired',
    defaultMessage: 'Enter a name',
  },
  shippingAmountInvalid: {
    id: 'admin.shipping.methods.amountInvalid',
    defaultMessage: 'Enter a valid amount',
  },
  shippingTaxStatusLabel: {
    id: 'admin.shipping.methods.taxStatus',
    defaultMessage: 'Tax status',
  },
  shippingTaxStatusTaxable: {
    id: 'admin.shipping.methods.taxStatusTaxable',
    defaultMessage: 'Taxable',
  },
  shippingTaxStatusNone: {
    id: 'admin.shipping.methods.taxStatusNone',
    defaultMessage: 'None',
  },
  shippingCostLabel: { id: 'admin.shipping.methods.cost', defaultMessage: 'Cost' },
  shippingAdditionalFeeHeading: {
    id: 'admin.shipping.methods.additionalFeeHeading',
    defaultMessage: 'Additional fee',
  },
  shippingAdditionalFeeLabel: {
    id: 'admin.shipping.methods.additionalFee',
    defaultMessage: 'Add an additional fee for every item in the order',
  },
  shippingAdditionalFeeCostLabel: {
    id: 'admin.shipping.methods.additionalFeeCost',
    defaultMessage: 'Additional fee cost',
  },
  shippingRequiresLabel: {
    id: 'admin.shipping.methods.requires',
    defaultMessage: 'Free shipping requires',
  },
  shippingRequiresNone: {
    id: 'admin.shipping.methods.requiresNone',
    defaultMessage: 'No requirement',
  },
  shippingRequiresCoupon: {
    id: 'admin.shipping.methods.requiresCoupon',
    defaultMessage: 'A valid free shipping coupon',
  },
  shippingRequiresMinAmount: {
    id: 'admin.shipping.methods.requiresMinAmount',
    defaultMessage: 'A minimum order amount',
  },
  shippingRequiresEither: {
    id: 'admin.shipping.methods.requiresEither',
    defaultMessage: 'A minimum order amount or coupon',
  },
  shippingRequiresBoth: {
    id: 'admin.shipping.methods.requiresBoth',
    defaultMessage: 'A minimum order amount and coupon',
  },
  shippingMinAmountLabel: {
    id: 'admin.shipping.methods.minAmount',
    defaultMessage: 'Minimum order amount',
  },
  shippingIgnoreDiscountsLabel: {
    id: 'admin.shipping.methods.ignoreDiscounts',
    defaultMessage: 'Apply minimum order rule before coupon discount.',
  },
  shippingIgnoreDiscountsDesc: {
    id: 'admin.shipping.methods.ignoreDiscountsDesc',
    defaultMessage: 'Free shipping would be available based on pre-discount order amount.',
  },
  shippingMethodAddNewHeading: {
    id: 'admin.shipping.methods.addNewHeading',
    defaultMessage: 'Add new shipping method',
  },
  shippingMethodEditHeading: {
    id: 'admin.shipping.methods.editHeading',
    defaultMessage: 'Edit shipping method',
  },
  shippingCostHelp: {
    id: 'admin.shipping.methods.costHelp',
    defaultMessage: 'Enter a cost excluding tax.',
  },
  shippingMinAmountHelp: {
    id: 'admin.shipping.methods.minAmountHelp',
    defaultMessage: 'Customers will need to spend this amount to get free shipping.',
  },

  // Shipping — local pickup form
  shippingPickupAddTitle: {
    id: 'admin.shipping.pickup.addTitle',
    defaultMessage: 'Add local pickup',
  },
  shippingPickupEditTitle: {
    id: 'admin.shipping.pickup.editTitle',
    defaultMessage: 'Edit local pickup',
  },
  shippingPickupFormDescription: {
    id: 'admin.shipping.pickup.formDescription',
    defaultMessage:
      'A local pickup option allows your customers to physically collect their orders at a location specified by you.',
  },
  shippingPickupOptionsTitle: {
    id: 'admin.shipping.pickup.optionsTitle',
    defaultMessage: 'Pickup options',
  },
  shippingPickupAddressTitle: {
    id: 'admin.shipping.pickup.addressTitle',
    defaultMessage: 'Pickup address',
  },
  shippingPickupSave: { id: 'admin.shipping.pickup.save', defaultMessage: 'Save' },
  shippingPickupName: { id: 'admin.shipping.pickup.name', defaultMessage: 'Pickup title' },
  shippingPickupNamePlaceholder: {
    id: 'admin.shipping.pickup.namePlaceholder',
    defaultMessage: 'e.g. in-store pickup',
  },
  shippingPickupNameRequired: {
    id: 'admin.shipping.pickup.nameRequired',
    defaultMessage: 'Pickup title is required.',
  },
  shippingPickupDetails: {
    id: 'admin.shipping.pickup.details',
    defaultMessage: 'Pickup instructions',
  },
  shippingPickupDetailsPlaceholder: {
    id: 'admin.shipping.pickup.detailsPlaceholder',
    defaultMessage: 'Add pickup instructions or extra details.',
  },
  shippingPickupStreet: {
    id: 'admin.shipping.pickup.street',
    defaultMessage: 'Street address',
  },
  shippingPickupAddressRequired: {
    id: 'admin.shipping.pickup.addressRequired',
    defaultMessage: 'Street address is required.',
  },
  shippingPickupCity: { id: 'admin.shipping.pickup.city', defaultMessage: 'City' },
  shippingPickupCityRequired: {
    id: 'admin.shipping.pickup.cityRequired',
    defaultMessage: 'City is required.',
  },
  shippingPickupPostcode: {
    id: 'admin.shipping.pickup.postcode',
    defaultMessage: 'Zip/postal code',
  },
  shippingPickupPostcodeRequired: {
    id: 'admin.shipping.pickup.postcodeRequired',
    defaultMessage: 'Zip/postal code is required.',
  },
  shippingPickupCountry: {
    id: 'admin.shipping.pickup.country',
    defaultMessage: 'Country',
  },
  shippingPickupCountryPlaceholder: {
    id: 'admin.shipping.pickup.countryPlaceholder',
    defaultMessage: 'Select country',
  },
  shippingPickupCountryRequired: {
    id: 'admin.shipping.pickup.countryRequired',
    defaultMessage: 'Country is required.',
  },
  shippingPickupSaveError: {
    id: 'admin.shipping.pickup.saveError',
    defaultMessage: 'Couldn’t save the pickup location. Please try again.',
  },
  shippingPickupLoadErrorTitle: {
    id: 'admin.shipping.pickup.loadErrorTitle',
    defaultMessage: 'Couldn’t load pickup location',
  },
  shippingPickupLoadErrorDescription: {
    id: 'admin.shipping.pickup.loadErrorDescription',
    defaultMessage: 'We couldn’t load this pickup location. Please go back and try again.',
  },

  // Payments & VAT — shared
  paymentsSave: { id: 'admin.payments.save', defaultMessage: 'Save' },
  paymentsSaving: { id: 'admin.payments.saving', defaultMessage: 'Saving…' },
  paymentsStatusActive: {
    id: 'admin.payments.status.active',
    defaultMessage: 'Active',
  },
  paymentsStatusInactive: {
    id: 'admin.payments.status.inactive',
    defaultMessage: 'Inactive',
  },
  paymentsActionConnect: {
    id: 'admin.payments.action.connect',
    defaultMessage: 'Connect',
  },
  paymentsActionManage: {
    id: 'admin.payments.action.manage',
    defaultMessage: 'Manage',
  },
  paymentsActionAddOption: {
    id: 'admin.payments.action.addOption',
    defaultMessage: 'Add option',
  },
  paymentsActionContinueSetup: {
    id: 'admin.payments.action.continueSetup',
    defaultMessage: 'Continue setup',
  },
  paymentsStatusPending: {
    id: 'admin.payments.status.pending',
    defaultMessage: 'Pending',
  },

  // Payments & VAT — overview
  paymentsTitle: { id: 'admin.payments.title', defaultMessage: 'Payment' },
  paymentsDescription: {
    id: 'admin.payments.description',
    defaultMessage:
      'These settings help you add payment options. You must select at least one payment option for customers to purchase your products.',
  },
  paymentsShoreTitle: {
    id: 'admin.payments.shore.title',
    defaultMessage: 'Shore Pay',
  },
  paymentsShoreSubtitle: {
    id: 'admin.payments.shore.subtitle',
    defaultMessage: 'Let customers pay using credit cards and digital wallets',
  },
  paymentsShoreBenefitStripe: {
    id: 'admin.payments.shore.benefit.stripe',
    defaultMessage: 'Powered by Stripe, a leading global payments provider.',
  },
  paymentsShoreBenefitFees: {
    id: 'admin.payments.shore.benefit.fees',
    defaultMessage: 'Enjoy the lowest fees on a range of popular payment methods',
  },
  paymentsShoreBenefitSupport: {
    id: 'admin.payments.shore.benefit.support',
    defaultMessage: 'Direct support through our team at Shore',
  },
  paymentsShoreBenefitPayouts: {
    id: 'admin.payments.shore.benefit.payouts',
    defaultMessage: 'Real-time payout insights and cashflow tracking',
  },
  paymentsManualCardTitle: {
    id: 'admin.payments.manual.card.title',
    defaultMessage: 'Manual payment',
  },
  paymentsManualCardSubtitle: {
    id: 'admin.payments.manual.card.subtitle',
    defaultMessage:
      'Let customers pay in person with cash, bank transfer, or other custom methods.',
  },
  paymentsVatCardTitle: {
    id: 'admin.payments.vat.card.title',
    defaultMessage: 'VAT & taxes',
  },
  paymentsVatCardSubtitle: {
    id: 'admin.payments.vat.card.subtitle',
    defaultMessage: 'Configure how VAT is applied to products and shipping.',
  },

  // Payments & VAT — Connect with Shore Pay
  paymentsShoreConnectTitle: {
    id: 'admin.payments.shore.connect.title',
    defaultMessage: 'Connect with Shore Pay',
  },
  paymentsShoreConnectDescription: {
    id: 'admin.payments.shore.connect.description',
    defaultMessage:
      'We need your business details before continuing to the payment setup flow. This information is used to create and verify your payment account.',
  },
  paymentsShoreFirstName: {
    id: 'admin.payments.shore.field.firstName',
    defaultMessage: 'First name',
  },
  paymentsShoreLastName: {
    id: 'admin.payments.shore.field.lastName',
    defaultMessage: 'Last name',
  },
  paymentsShoreCompanyName: {
    id: 'admin.payments.shore.field.companyName',
    defaultMessage: 'Company Name',
  },
  paymentsShoreStreetAddress: {
    id: 'admin.payments.shore.field.streetAddress',
    defaultMessage: 'Street Address',
  },
  paymentsShoreCity: {
    id: 'admin.payments.shore.field.city',
    defaultMessage: 'City',
  },
  paymentsShoreZipCode: {
    id: 'admin.payments.shore.field.zipCode',
    defaultMessage: 'Zip Code',
  },
  paymentsShoreCountry: {
    id: 'admin.payments.shore.field.country',
    defaultMessage: 'Country',
  },
  paymentsShoreCountryPlaceholder: {
    id: 'admin.payments.shore.field.country.placeholder',
    defaultMessage: 'Select a country',
  },
  paymentsShoreEmail: {
    id: 'admin.payments.shore.field.email',
    defaultMessage: 'Email',
  },
  paymentsShoreVatId: {
    id: 'admin.payments.shore.field.vatId',
    defaultMessage: 'VAT id',
  },
  paymentsShoreTaxNumber: {
    id: 'admin.payments.shore.field.taxNumber',
    defaultMessage: 'Tax number',
  },
  paymentsShoreFirstNameRequired: {
    id: 'admin.payments.shore.field.firstName.required',
    defaultMessage: 'Enter a first name.',
  },
  paymentsShoreLastNameRequired: {
    id: 'admin.payments.shore.field.lastName.required',
    defaultMessage: 'Enter a last name.',
  },
  paymentsShoreStreetAddressRequired: {
    id: 'admin.payments.shore.field.streetAddress.required',
    defaultMessage: 'Enter a street address.',
  },
  paymentsShoreCityRequired: {
    id: 'admin.payments.shore.field.city.required',
    defaultMessage: 'Enter a city.',
  },
  paymentsShoreZipCodeRequired: {
    id: 'admin.payments.shore.field.zipCode.required',
    defaultMessage: 'Enter a zip code.',
  },
  paymentsShoreCountryRequired: {
    id: 'admin.payments.shore.field.country.required',
    defaultMessage: 'Select a country.',
  },
  paymentsShoreConnecting: {
    id: 'admin.payments.shore.connecting',
    defaultMessage: 'Connecting…',
  },
  paymentsShoreTermsPrefix: {
    id: 'admin.payments.shore.terms.prefix',
    defaultMessage: 'By connecting you agree to',
  },
  paymentsShoreTermsShorePay: {
    id: 'admin.payments.shore.terms.shorePay',
    defaultMessage: 'Shore Pay Terms',
  },
  paymentsShoreTermsJoiner: {
    id: 'admin.payments.shore.terms.joiner',
    defaultMessage: 'and to',
  },
  paymentsShoreTermsStripe: {
    id: 'admin.payments.shore.terms.stripe',
    defaultMessage: 'Stripe Terms',
  },
  paymentsShoreConnectError: {
    id: 'admin.payments.shore.connect.error',
    defaultMessage: 'Couldn’t connect to Shore Pay. Please try again.',
  },
  paymentsShoreEmailRequired: {
    id: 'admin.payments.shore.field.email.required',
    defaultMessage: 'Enter an email address.',
  },
  paymentsShoreEmailInvalid: {
    id: 'admin.payments.shore.field.email.invalid',
    defaultMessage: 'Enter a valid email address.',
  },
  paymentsShoreManageTitle: {
    id: 'admin.payments.shore.manage.title',
    defaultMessage: 'Manage Shore Pay',
  },
  paymentsShoreManageDescription: {
    id: 'admin.payments.shore.manage.description',
    defaultMessage: 'Enable or disable Shore Pay and finish verifying your account.',
  },
  paymentsShoreKycLabel: {
    id: 'admin.payments.shore.kyc.label',
    defaultMessage: 'Account verification',
  },
  paymentsShoreKycLink: {
    id: 'admin.payments.shore.kyc.link',
    defaultMessage: 'Complete Shore verification',
  },
  paymentsShoreUpdateError: {
    id: 'admin.payments.shore.update.error',
    defaultMessage: 'Couldn’t update Shore Pay. Please try again.',
  },

  // Payments & VAT — Manual payment
  paymentsManualTitle: {
    id: 'admin.payments.manual.title',
    defaultMessage: 'Manual payment',
  },
  paymentsManualDescription: {
    id: 'admin.payments.manual.description',
    defaultMessage:
      "For when customers pay you offline (e.g. when picking up a product in person) - you'll mark each order as paid yourself. For online payments, use Shore Pay.",
  },
  paymentsStatusLabel: {
    id: 'admin.payments.manual.status.label',
    defaultMessage: 'Status',
  },
  paymentsManualName: {
    id: 'admin.payments.manual.name.label',
    defaultMessage: 'Payment method name',
  },
  paymentsManualNamePlaceholder: {
    id: 'admin.payments.manual.name.placeholder',
    defaultMessage: 'e.g. Pay in person (cash, card, digital wallet)',
  },
  paymentsManualNameRequired: {
    id: 'admin.payments.manual.name.required',
    defaultMessage: 'Enter a name.',
  },
  paymentsManualDescriptionLabel: {
    id: 'admin.payments.manual.description.label',
    defaultMessage: 'Description',
  },
  paymentsManualDescriptionRequired: {
    id: 'admin.payments.manual.description.required',
    defaultMessage: 'Enter a description.',
  },
  paymentsManualDescriptionHelp: {
    id: 'admin.payments.manual.description.help',
    defaultMessage:
      'Add a description which will be displayed to customers when choosing the payment option.',
  },
  paymentsManualInstructionsLabel: {
    id: 'admin.payments.manual.instructions.label',
    defaultMessage: 'Instructions',
  },
  paymentsManualInstructionsHelp: {
    id: 'admin.payments.manual.instructions.help',
    defaultMessage:
      'Instructions will be displayed on the “Thank you” page and emails after the customer has placed their order.',
  },
  paymentsManualLocalPickup: {
    id: 'admin.payments.manual.localPickup.label',
    defaultMessage: 'Enable for local pick up only',
  },
  paymentsManualSaveError: {
    id: 'admin.payments.manual.saveError',
    defaultMessage: 'Couldn’t save the payment method. Please try again.',
  },

  // Payments & VAT — VAT & taxes
  paymentsVatTitle: {
    id: 'admin.payments.vat.title',
    defaultMessage: 'VAT & taxes',
  },
  paymentsVatDescription: {
    id: 'admin.payments.vat.description',
    defaultMessage: 'Configure how VAT is applied to your products and shipping.',
  },
  paymentsVatStatusLabel: {
    id: 'admin.payments.vat.status.label',
    defaultMessage: 'VAT status',
  },
  paymentsVatRateLabel: {
    id: 'admin.payments.vat.rate.label',
    defaultMessage: 'Standard VAT rate',
  },
  paymentsVatRateRequired: {
    id: 'admin.payments.vat.rate.required',
    defaultMessage: 'Field is required',
  },
  paymentsVatRateInvalid: {
    id: 'admin.payments.vat.rate.invalid',
    defaultMessage: 'Enter the rate as a number from 0 to 100 (e.g. 21 or 12.5).',
  },
  paymentsVatApplyShippingLabel: {
    id: 'admin.payments.vat.applyShipping.label',
    defaultMessage: 'Apply VAT to shipping',
  },
  paymentsVatApplyShippingDescription: {
    id: 'admin.payments.vat.applyShipping.description',
    defaultMessage: 'VAT will also be applied to shipping costs during checkout.',
  },
  paymentsVatSaveError: {
    id: 'admin.payments.vat.saveError',
    defaultMessage: 'Couldn’t save the VAT settings. Please try again.',
  },
});
